import { useState, useEffect, useCallback } from 'react';
import {
  TextInput,
  Select,
  Alert,
  Badge,
  Spinner,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from 'flowbite-react';
import { HiSearch, HiEye, HiFilter, HiX } from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getUserErrorMessage } from '../api';
import {
  activityLogApi,
  EVENT_CATEGORIES,
  ENTITY_TYPES,
} from '../api/activityLog';
import type { ActivityLog, ActivityFilters } from '../api/activityLog';
import { ActivityDetailModal } from '../components/activity/ActivityDetailModal';

const PAGE_SIZE = 50;

const CATEGORY_COLORS: Record<string, string> = {
  organization: 'success',
  lea: 'success',
  school: 'purple',
  person: 'info',
  staff: 'warning',
  security: 'failure',
  address: 'gray',
  user: 'indigo',
};

export const ActivityMonitorPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Redirect non-superAdmin users
  useEffect(() => {
    if (currentUser && currentUser.role !== 'superAdmin') {
      navigate('/access-denied');
    }
  }, [currentUser, navigate]);

  // Data state
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Detail modal state
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Build filters object
  const buildFilters = useCallback((): ActivityFilters => {
    const filters: ActivityFilters = {};
    if (searchTerm) filters.search = searchTerm;
    if (selectedCategory) filters.eventCategory = selectedCategory;
    if (selectedEntityType) filters.entityType = selectedEntityType;
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    return filters;
  }, [searchTerm, selectedCategory, selectedEntityType, startDate, endDate]);

  // Fetch activities
  const fetchActivities = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await activityLogApi.list(buildFilters(), page, PAGE_SIZE);
      setActivities(response.data);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to load activity logs'));
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  // Debounced fetch when filters change — reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchActivities(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory, selectedEntityType, startDate, endDate]);

  // Fetch when page changes (user clicks pagination)
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchActivities(page);
  };

  const formatTimestamp = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewDetail = (activityLogId: number) => {
    setSelectedActivityId(activityLogId);
    setShowDetailModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedEntityType('');
    setStartDate('');
    setEndDate('');
    setShowAdvancedFilters(false);
  };

  const hasActiveFilters = !!(selectedCategory || selectedEntityType || startDate || endDate);

  return (
    <div className="container mx-auto p-8">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Activity Monitor
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Browse and search the system activity log
        </p>
      </div>

      {error && (
        <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
          <span className="font-medium">Error!</span> {error}
        </Alert>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {/* Search and primary filter row */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="relative flex-1 max-w-md">
              <div className="absolute top-1/2 -translate-y-1/2 left-0 flex items-center pl-3 pointer-events-none z-10">
                <HiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <TextInput
                placeholder="Search by description, event type, or actor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                aria-label="Search activities"
              />
            </div>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-48"
              aria-label="Filter by category"
            >
              <option value="">All Categories</option>
              {EVENT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </Select>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  showAdvancedFilters
                    ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-400'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <HiFilter className="inline-block mr-1 h-4 w-4" />
                Filters
                {hasActiveFilters && (
                  <Badge color="blue" size="xs" className="ml-1 inline-flex">!</Badge>
                )}
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  <HiX className="inline-block mr-1 h-3 w-3" />
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Entity Type
                </label>
                <Select
                  value={selectedEntityType}
                  onChange={(e) => setSelectedEntityType(e.target.value)}
                >
                  <option value="">All Entity Types</option>
                  {ENTITY_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full text-sm rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full text-sm rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Results count + table */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
          {totalCount.toLocaleString()} total activities
        </div>

        {loading && activities.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table hoverable>
                <TableHead>
                  <TableHeadCell className="w-36">Timestamp</TableHeadCell>
                  <TableHeadCell className="w-28">Category</TableHeadCell>
                  <TableHeadCell className="w-40">Event</TableHeadCell>
                  <TableHeadCell>Description</TableHeadCell>
                  <TableHeadCell className="w-32">Actor</TableHeadCell>
                  <TableHeadCell className="w-16"></TableHeadCell>
                </TableHead>
                <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {activities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <p className="text-gray-500 dark:text-gray-400">
                          No activities found matching your filters.
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activities.map((activity) => (
                      <TableRow
                        key={activity.activityLogId}
                        className="bg-white dark:bg-gray-800"
                      >
                        <TableCell className="whitespace-nowrap text-xs text-gray-900 dark:text-gray-100">
                          {formatTimestamp(activity.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Badge color={CATEGORY_COLORS[activity.eventCategory] || 'gray'} size="xs">
                            {activity.eventCategory}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {activity.eventType}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {activity.entityType}#{activity.entityId}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-md text-sm text-gray-900 dark:text-gray-100 break-words">
                            {activity.actionDescription}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <div className="text-gray-900 dark:text-white">
                              {activity.actorName || `User #${activity.actorId}`}
                            </div>
                            <Badge color="gray" size="xs">
                              {activity.actorType}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleViewDetail(activity.activityLogId)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 rounded transition-colors"
                            title="View details"
                          >
                            <HiEye className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((currentPage - 1) * PAGE_SIZE + 1).toLocaleString()} to{' '}
                  {Math.min(currentPage * PAGE_SIZE, totalCount).toLocaleString()} of{' '}
                  {totalCount.toLocaleString()} entries
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  showIcons
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      <ActivityDetailModal
        activityLogId={selectedActivityId}
        show={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedActivityId(null);
        }}
      />
    </div>
  );
};
